// Internship Pathfinder — 11th LearnixTa pathway.
// Official government + company internship routes from src/data/internshipPathways.json.
// Only publishing_status === "ready" records are shown publicly; review/hold stay hidden.
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import PathwayDisclaimer from '../components/PathwayDisclaimer';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Briefcase, Search, X, ExternalLink, ShieldCheck, Compass,
  ClipboardCheck, Globe, Send, BadgeCheck, Landmark, ChevronDown,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import internshipData from '../data/internshipPathways.json';
import { GuideAccordions, RouteLinks, StateGuide, Value, Row as GuideRow } from '../components/resource-pathways/GuideParts';

const SOURCES = Object.fromEntries((internshipData.source_register || []).map((s) => [s.id, s]));
/* application_state statuses that must never read as an open application */
const STATE_LABEL = {
  published_2026_window_closed: 'Published 2026 window closed',
  reviewed_phase_deadline_passed: 'Reviewed phase deadline passed',
  access_limited_not_verified: 'Access limited — not verified',
};
const stateOf = (r) => STATE_LABEL[r.application_state?.status] || null;
const INTERNAL_ROUTES = {
  life_skills: '/life-skills', existing_skilled_service_or_blue_collar_pathway: '/blue-collar', existing_entrepreneurship_pathway: '/ownpath',
  existing_agriculture_pathway: '/agriculture', fresher_launchpad: '/pathways/fresher-launchpad', career_switch_growth: '/pathways/career-switch-growth',
};

const ALL_RECORDS = internshipData.pathways || [];
const READY = ALL_RECORDS.filter((r) => r.publishing_status === 'ready');
const PMIS = READY.find((r) => r.id === 'pm-internship-scheme') || null;
const COMPANIES = READY.filter((r) => r.id !== 'pm-internship-scheme');
// review + hold records: names only, never linked, never described.
const WATCHLIST = ALL_RECORDS
  .filter((r) => r.publishing_status === 'review' || r.publishing_status === 'hold')
  .map((r) => r.organization)
  .sort((a, b) => a.localeCompare(b));
const PAGE_SIZE = 9;

const REVIEWED_LABEL = (() => {
  const d = new Date(`${internshipData.last_reviewed}T00:00:00`);
  return isNaN(d) ? internshipData.last_reviewed
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
})();

const domainOf = (url) => { try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; } };

const CATEGORIES = [
  { key: 'all', label: 'All', test: () => true },
  { key: 'tech', label: 'Technology', test: (t) => /\b(software|tech|developer|engineer(ing)? intern|it |cloud|cyber|data|ai |digital|comput)/.test(t) || /(google|meta|microsoft|amazon|ibm|intel|nvidia|apple|adobe|oracle|salesforce|sap|cisco|qualcomm|infosys|tcs|wipro|hcl|tech mahindra|accenture|capgemini|cognizant|zoho|flipkart|paytm|swiggy|zomato|uber|samsung)/.test(t) },
  { key: 'engineering', label: 'Engineering & Manufacturing', test: (t) => /(manufactur|automotive|aerospace|energy|electrical|mechanical|plant|industrial|defence|defense|railways|steel|power|oil|gas|drdo|isro|bhel|ntpc|ongc|tata motors|mahindra|maruti|bosch|siemens|ge |larsen|l&t|hero|bajaj|ashok leyland)/.test(t) },
  { key: 'business', label: 'Business & Operations', test: (t) => /(business|operations|management|human resources|hr |marketing|sales|strategy|corporate|administration)/.test(t) },
  { key: 'finance', label: 'Finance & Consulting', test: (t) => /(bank|finance|financial|consult|audit|invest|insurance|accounting|tax|deloitte|kpmg|pwc|ey |ernst|mckinsey|bcg|bain|goldman|morgan|jpmorgan|hdfc|icici|sbi|axis|rbi)/.test(t) },
  { key: 'product', label: 'Product, Design & Research', test: (t) => /(product|design|ux|ui |research|scientist|innovation|r&d)/.test(t) },
  { key: 'consumer', label: 'Consumer & Supply Chain', test: (t) => /(retail|consumer|fmcg|supply chain|logistics|e-?commerce|food|beverage|unilever|nestl|itc |procter|p&g|britannia|dabur|marico|reliance retail|dmart|bigbasket|delhivery)/.test(t) },
];

const recordText = (r) =>
  `${r.organization} ${r.card_preview} ${r.best_for} ${r.eligibility_and_age} ${r.availability} ${r.program_type || ''}`.toLowerCase();

const categoriesOf = (r) => {
  const t = recordText(r);
  return CATEGORIES.filter((c) => c.key !== 'all' && c.test(t)).map((c) => c.key);
};

const CAT_INDEX = new Map(COMPANIES.map((r) => [r.id, categoriesOf(r)]));

/* External CTA — exact JSON URL, new tab, safe attrs, domain shown */
const OfficialLink = ({ record, className = '', children }) => (
  <a
    href={record.official_url}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={`Visit the official ${record.organization} portal (opens in a new tab, leaving LearnixTa)`}
    className={className}
    data-testid={`ipf-official-link-${record.id}`}
  >
    {children} <ExternalLink size={13} className="shrink-0" aria-hidden="true" />
  </a>
);

/* Accessible details modal */
const DetailsModal = ({ record, onClose }) => {
  const rootRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key !== 'Tab' || !rootRef.current) return;
      const f = rootRef.current.querySelectorAll('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])');
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const Row = ({ label, value }) => (
    <div>
      <p className="text-[11px] uppercase tracking-widest font-extrabold text-teal-700 mb-1">{label}</p>
      <p className="text-[15px] text-gray-700 leading-relaxed">{value}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="ipf-details-modal">
      <div className="absolute inset-0 bg-slate-900/60" onClick={onClose} aria-hidden="true" />
      <div
        ref={rootRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${record.organization} internship pathway details`}
        className="relative bg-white w-full sm:max-w-2xl max-h-[92vh] sm:max-h-[85vh] rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden"
      >
        <div className="flex items-start justify-between gap-3 px-5 md:px-7 pt-5 pb-4 border-b border-gray-100">
          <div>
            <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-200 mb-2">
              <BadgeCheck size={11} aria-hidden="true" /> Official career portal
            </span>
            <h3 className="text-lg md:text-xl font-bold text-gray-900">{record.organization}</h3>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close details"
            className="shrink-0 w-11 h-11 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-600"
            data-testid="ipf-details-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-5 md:px-7 py-5 space-y-5">
          {stateOf(record) && (
            <p className="text-[13px] font-bold text-rose-900 bg-rose-50 border border-rose-200 rounded-2xl px-4 py-3" data-testid="ipf-details-state">
              {stateOf(record)} · checked {record.application_state?.checked_on}. Not an open application.
            </p>
          )}
          <Row label="About this pathway" value={record.card_preview} />
          <Row label="Best for" value={record.best_for} />
          <Row label="Eligibility & age" value={record.eligibility_and_age} />
          <Row label="Availability" value={record.availability} />
          {Object.entries(record.full_preview || {}).map(([k, v]) => <GuideRow key={k} label={k.replace(/_/g, ' ')} value={v} reg={SOURCES} />)}
          {record.india_location_check && <Row label="India location check" value={record.india_location_check} />}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Globe size={14} className="text-teal-600 shrink-0" aria-hidden="true" />
            Official destination: <span className="font-bold text-gray-900">{domainOf(record.official_url)}</span>
          </div>
          {record.verification && (
            <div className="text-[13px] text-gray-600 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 space-y-1" data-testid="ipf-details-verification">
              <p><span className="font-bold text-gray-800">Content reviewed:</span> {record.verification.reviewed_on || record.content_reviewed_on} · {record.verification.status}</p>
              <p><span className="font-bold text-gray-800">Live opening verified:</span> {record.verification.live_opening_verified ? 'Yes' : 'No — directory reference only.'}</p>
              {(record.verification.source_ids || []).map((i) => SOURCES[i]).filter(Boolean).length > 0 && (
                <p>Sources: {(record.verification.source_ids || []).map((i) => SOURCES[i]).filter(Boolean).map((s, i) => <span key={s.id}>{i > 0 && ' · '}<a href={s.url} target="_blank" rel="noopener noreferrer" className="underline text-teal-700">{s.title}</a></span>)}</p>
              )}
              {record.historical_publication_conditions?.note && <p className="text-gray-500">{record.historical_publication_conditions.note}</p>}
            </div>
          )}
          <p className="text-[13px] text-amber-900 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 leading-relaxed">
            Eligibility and openings can change. Confirm the current requirements on the official website before applying.
          </p>
        </div>

        <div className="px-5 md:px-7 py-4 border-t border-gray-100 bg-gray-50/70">
          <OfficialLink
            record={record}
            className="inline-flex w-full sm:w-auto items-center justify-center gap-2 min-h-[44px] px-6 py-3 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-800"
          >
            {record.cta_label || 'Visit official portal'}
          </OfficialLink>
          <p className="text-[12px] text-gray-500 mt-2">
            This link opens {domainOf(record.official_url)} in a new tab — you are leaving LearnixTa. Applications are completed on the organization&rsquo;s own website.
          </p>
        </div>
      </div>
    </div>
  );
};

export default function InternshipPathfinder() {
  const [query, setQuery] = useState('');
  const [cat, setCat] = useState('all');
  const [shown, setShown] = useState(PAGE_SIZE);
  const [detail, setDetail] = useState(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Internship Pathfinder | LearnixTa';
    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute('content');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'Explore official government and company internship pathways, compare eligibility and continue to the official application portal.');
    return () => { document.title = prevTitle; if (prevDesc !== undefined && meta) meta.setAttribute('content', prevDesc || ''); };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return COMPANIES.filter((r) => {
      if (cat !== 'all' && !(CAT_INDEX.get(r.id) || []).includes(cat)) return false;
      if (!q) return true;
      return recordText(r).includes(q);
    });
  }, [query, cat]);

  useEffect(() => { setShown(PAGE_SIZE); }, [query, cat]);

  const visible = filtered.slice(0, shown);
  const hasFilters = query.trim() !== '' || cat !== 'all';

  const openDetail = useCallback((record, e) => {
    triggerRef.current = e?.currentTarget || null;
    setDetail(record);
  }, []);
  const closeDetail = useCallback(() => {
    setDetail(null);
    triggerRef.current?.focus();
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const factChip = 'inline-flex items-center gap-1.5 text-[12px] font-bold text-teal-900 bg-teal-50 border border-teal-200 rounded-full px-3 py-1.5';

  return (
    <div className="min-h-screen bg-[#FFF9F1]">
      <Navbar />

      <main className="pt-28 md:pt-32 pb-16">
        {/* ── Hero ─────────────────────────────────────────── */}
        <section className="max-w-7xl mx-auto px-4 md:px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:text-teal-800 mb-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-600 rounded"
            data-testid="ipf-back-link"
          >
            <ArrowLeft size={15} aria-hidden="true" /> Back to Pathways
          </Link>

          <div className="max-w-3xl">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-teal-700 mb-4">
              Internship Pathway · Official Destinations
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight leading-[1.05]" data-testid="ipf-heading">
              Find an internship pathway that fits you.
            </h1>
            <p className="text-base md:text-lg text-gray-600 mt-5 leading-relaxed">
              Explore government schemes and student programs from leading companies. Understand the basic
              requirements, then continue to the official portal for current openings and applications.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => scrollTo('ipf-directory')}
                className="inline-flex items-center gap-2 min-h-[44px] px-6 py-3 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-800"
                data-testid="ipf-cta-explore"
              >
                Explore opportunities
              </button>
              <button
                type="button"
                onClick={() => scrollTo('ipf-how-it-works')}
                className="inline-flex items-center gap-2 min-h-[44px] px-6 py-3 rounded-full border-2 border-teal-600 text-teal-700 hover:bg-teal-50 font-bold text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-800"
                data-testid="ipf-cta-how"
              >
                How this works
              </button>
            </div>
            <p className="mt-5 text-[13px] text-gray-500 flex items-center gap-2 flex-wrap" data-testid="ipf-trust-line">
              <ShieldCheck size={14} className="text-teal-600 shrink-0" aria-hidden="true" />
              Official destinations only · No applications collected by LearnixTa · Reviewed {REVIEWED_LABEL}
            </p>
          </div>
        </section>

        {/* ── How this works ───────────────────────────────── */}
        <section id="ipf-how-it-works" className="max-w-7xl mx-auto px-4 md:px-6 mt-16 scroll-mt-40">
          <h2 className="text-base md:text-lg font-bold text-gray-900 uppercase tracking-widest mb-6">How this works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { Icon: Compass, t: 'Explore opportunities', d: 'Search government and company internship pathways.' },
              { Icon: ClipboardCheck, t: 'Check your fit', d: 'Read the suitability, eligibility and availability information.' },
              { Icon: Globe, t: 'Open the official portal', d: 'Continue using the clearly labelled external link.' },
              { Icon: Send, t: 'Apply there', d: 'Complete and track your application on the organization\u2019s website.' },
            ].map((s, i) => (
              <div key={i} className="bg-white border-2 border-teal-100 rounded-3xl p-5" data-testid={`ipf-step-${i + 1}`}>
                <span className="w-10 h-10 rounded-2xl bg-teal-600 text-white flex items-center justify-center mb-3">
                  <s.Icon size={18} aria-hidden="true" />
                </span>
                <p className="font-bold text-gray-900 mb-1">{i + 1}. {s.t}</p>
                <p className="text-sm text-gray-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Featured PMIS ────────────────────────────────── */}
        {PMIS && (
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-14">
            <div className="bg-white border-2 border-teal-200 rounded-3xl p-6 md:p-8 shadow-sm" data-testid="ipf-pmis-card">
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-full bg-teal-600 text-white">
                  <Landmark size={12} aria-hidden="true" /> Featured Government Scheme
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{PMIS.organization}</h2>
              <p className="text-[15px] text-gray-700 mt-3 leading-relaxed max-w-3xl">{PMIS.card_preview}</p>

              <div className="mt-5 flex flex-wrap gap-2" data-testid="ipf-pmis-facts">
                <span className={factChip}>{PMIS.publishing_status_label}</span>
                <span className={factChip}>Last reviewed: {REVIEWED_LABEL}</span>
              </div>

              <div className="mt-6 grid md:grid-cols-3 gap-5">
                <div>
                  <p className="text-[11px] uppercase tracking-widest font-extrabold text-teal-700 mb-1.5">Best for</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{PMIS.best_for}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-widest font-extrabold text-teal-700 mb-1.5">Eligibility & age</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{PMIS.eligibility_and_age}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-widest font-extrabold text-teal-700 mb-1.5">Availability</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{PMIS.availability}</p>
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
                <OfficialLink
                  record={PMIS}
                  className="inline-flex items-center justify-center gap-2 min-h-[44px] px-6 py-3 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-800"
                >
                  Visit official PMIS portal
                </OfficialLink>
                <p className="text-[13px] text-gray-500 flex items-center gap-1.5">
                  <Globe size={13} className="text-teal-600 shrink-0" aria-hidden="true" />
                  {domainOf(PMIS.official_url)} · Applications are completed on the official Government portal.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* ── Safety notice ────────────────────────────────── */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 mt-10">
          <p className="text-sm text-gray-800 bg-amber-50 border-2 border-amber-200 rounded-2xl px-5 py-4 leading-relaxed" data-testid="ipf-safety-notice">
            <ShieldCheck size={15} className="inline -mt-0.5 mr-1.5 text-amber-700" aria-hidden="true" />
            Apply only through the linked official portal. LearnixTa does not collect internship fees, documents
            or applications. Never pay anyone who promises guaranteed selection.
          </p>
        </section>

        {/* ── Directory ────────────────────────────────────── */}
        <section id="ipf-directory" className="max-w-7xl mx-auto px-4 md:px-6 mt-14 scroll-mt-40">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Explore company internship pathways</h2>
          <p className="text-[15px] text-gray-600 mt-2 max-w-2xl leading-relaxed">
            Search by company or interest. Availability changes frequently, so always check the current details
            on the official careers site.
          </p>
          <p className="text-[13px] font-semibold text-gray-500 mt-2" data-testid="ipf-summary-counts">
            Official pathways: {READY.length} · Companies to monitor independently: {WATCHLIST.length}
          </p>

          {/* Search + filters */}
          <div className="mt-6">
            <div className="relative max-w-xl">
              <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={'Search companies, interests or eligibility\u2026'}
                aria-label="Search internship pathways"
                className="w-full min-h-[48px] pl-11 pr-11 py-3 rounded-full bg-white border-2 border-teal-100 focus:border-teal-500 outline-none text-[15px] text-gray-900 placeholder:text-gray-400 transition-colors"
                data-testid="ipf-search-input"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors"
                  data-testid="ipf-search-clear"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2" role="group" aria-label="Filter internship pathways">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setCat(c.key)}
                  aria-pressed={cat === c.key}
                  className={`min-h-[44px] px-4 py-2 rounded-full text-[13px] font-bold border-2 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-700 ${cat === c.key
                    ? 'bg-teal-600 border-teal-600 text-white'
                    : 'bg-white border-teal-100 text-gray-700 hover:border-teal-400'}`}
                  data-testid={`ipf-filter-${c.key}`}
                >
                  {c.label}
                </button>
              ))}
              {hasFilters && (
                <button
                  type="button"
                  onClick={() => { setQuery(''); setCat('all'); }}
                  className="min-h-[44px] px-4 py-2 text-[13px] font-bold text-teal-700 hover:text-teal-900 underline underline-offset-4"
                  data-testid="ipf-clear-filters"
                >
                  Clear all filters
                </button>
              )}
            </div>

            <p className="mt-4 text-sm font-semibold text-gray-600" aria-live="polite" data-testid="ipf-results-count">
              {filtered.length} of {COMPANIES.length} company pathways{PMIS ? ' (plus the featured PMIS scheme above)' : ''}
            </p>
          </div>

          {/* Cards */}
          {visible.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {visible.map((r) => (
                <article
                  key={r.id}
                  className="bg-white border-2 border-teal-100 hover:border-teal-300 rounded-3xl p-5 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-lg motion-reduce:transform-none"
                  data-testid={`ipf-card-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-bold text-gray-900 text-lg leading-snug">{r.organization}</h3>
                    <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold tracking-wide uppercase px-2 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
                      <BadgeCheck size={10} aria-hidden="true" /> Official career portal
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed line-clamp-4">{r.card_preview}</p>
                  {stateOf(r) && (
                    <span className="mt-3 inline-flex w-fit text-[11px] font-bold px-2.5 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-800" data-testid={`ipf-state-${r.id}`}>
                      {stateOf(r)}
                    </span>
                  )}
                  <p className="text-[13px] text-gray-700 mt-3 line-clamp-2">
                    <span className="font-bold text-teal-800">Best for: </span>{r.best_for}
                  </p>
                  <p className="text-[13px] text-gray-500 mt-1.5 line-clamp-2">
                    <span className="font-bold text-gray-700">Availability: </span>{r.availability}
                  </p>
                  <div className="mt-auto pt-4 flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={(e) => openDetail(r, e)}
                      className="min-h-[44px] px-4 py-2 rounded-full border-2 border-teal-600 text-teal-700 hover:bg-teal-50 font-bold text-[13px] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-800"
                      data-testid={`ipf-details-btn-${r.id}`}
                    >
                      View details
                    </button>
                    <OfficialLink
                      record={r}
                      className="inline-flex items-center gap-1.5 min-h-[44px] px-4 py-2 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-bold text-[13px] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-800"
                    >
                      {r.cta_label || 'Visit official portal'}
                    </OfficialLink>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-2">{domainOf(r.official_url)} · opens in a new tab</p>
                </article>
              ))}
            </div>
          ) : (
            <div className="mt-8 bg-white border-2 border-dashed border-teal-200 rounded-3xl px-6 py-12 text-center" data-testid="ipf-empty-state">
              <Briefcase size={28} className="mx-auto text-teal-500 mb-3" aria-hidden="true" />
              <p className="font-bold text-gray-900">No pathways match your search.</p>
              <p className="text-sm text-gray-600 mt-1">Try a different company name or interest, or clear the filters.</p>
              <button
                type="button"
                onClick={() => { setQuery(''); setCat('all'); }}
                className="mt-4 min-h-[44px] px-5 py-2.5 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm transition-colors"
                data-testid="ipf-empty-clear"
              >
                Clear all filters
              </button>
            </div>
          )}

          {filtered.length > shown && (
            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={() => setShown((n) => n + PAGE_SIZE)}
                className="inline-flex items-center gap-2 min-h-[48px] px-7 py-3 rounded-full border-2 border-teal-600 text-teal-700 hover:bg-teal-50 font-bold text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-800"
                data-testid="ipf-load-more"
              >
                Load more opportunities <ChevronDown size={15} aria-hidden="true" />
              </button>
              <p className="text-[12px] text-gray-500 mt-2">Showing {visible.length} of {filtered.length}</p>
            </div>
          )}
        </section>

        {/* ── All-India guide modules (v4.1.0) ───────────────── */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 mt-14" data-testid="ipf-guide-section">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{internshipData.india_coverage?.heading || 'Find a practical route in your state or union territory'}</h2>
          <p className="text-[15px] text-gray-600 mt-2 max-w-2xl leading-relaxed">
            Reviewed {REVIEWED_LABEL}. Discovery guidance and directories — not a live vacancy feed. Automation notes are labelled scenarios.
          </p>
          <div className="mt-6 bg-white border-2 border-teal-100 rounded-3xl p-5 md:p-6 mb-6">
            <StateGuide coverage={internshipData.india_coverage} reg={SOURCES} tid="ipf" />
          </div>
          <GuideAccordions tid="ipf" modules={[
            internshipData.route_type_comparison && { id: 'route_types', title: 'Jobs, internships, apprenticeships, courses and volunteering — how they differ', body: Value({ v: internshipData.route_type_comparison, reg: SOURCES }) },
            internshipData.learning_journey && { id: 'journey', title: 'Learning journey', body: Value({ v: internshipData.learning_journey, reg: SOURCES }) },
            internshipData.quality_checklist && { id: 'quality', title: 'Quality checklist before accepting', body: Value({ v: internshipData.quality_checklist, reg: SOURCES }) },
            internshipData.application_readiness && { id: 'readiness', title: 'Application readiness', body: Value({ v: internshipData.application_readiness, reg: SOURCES }) },
            internshipData.practice_alternatives && { id: 'practice', title: internshipData.practice_alternatives.heading || 'If you do not secure an internship yet', body: Value({ v: internshipData.practice_alternatives, reg: SOURCES }) },
            internshipData.research_and_policy_practice && { id: 'research', title: 'Research and policy practice', body: Value({ v: internshipData.research_and_policy_practice, reg: SOURCES }) },
            internshipData.ai_workforce_reality && { id: 'ai', title: internshipData.ai_workforce_reality.heading || 'AI workforce reality — labelled scenarios', body: Value({ v: internshipData.ai_workforce_reality, reg: SOURCES }) },
            internshipData.india_guidance && { id: 'india', title: internshipData.india_guidance.heading || 'India guidance', body: Value({ v: internshipData.india_guidance, reg: SOURCES }) },
            internshipData.india_decision_guidance && { id: 'decision', title: 'India decision guidance — location, documents, costs', body: Value({ v: internshipData.india_decision_guidance, reg: SOURCES }) },
            internshipData.sector_exploration && { id: 'sectors', title: 'Sector exploration — examples, not vacancies', body: Value({ v: internshipData.sector_exploration, reg: SOURCES }) },
            internshipData.national_routes_added && { id: 'national', title: 'National routes added in this edition', body: <ul className="list-disc pl-5 space-y-1">{internshipData.national_routes_added.map((id) => { const r = ALL_RECORDS.find((x) => x.id === id); return <li key={id}>{r ? `${r.organization} — ${r.publishing_status_label}${stateOf(r) ? ` · ${stateOf(r)}` : ''}` : id}</li>; })}</ul> },
            (internshipData.review_method || internshipData.release_notes) && { id: 'review', title: 'Content review, limits and change notes', body: Value({ v: { ...(internshipData.review_method || {}), release_notes: internshipData.release_notes, publishing_status_definitions: internshipData.publishing_status_definitions }, reg: SOURCES }) },
            { id: 'sources', title: `Sources (${internshipData.source_register?.length || 0})`, body: <ol className="space-y-1.5 text-sm">{(internshipData.source_register || []).map((s, i) => <li key={s.id}>{i + 1}. <a href={s.url} target="_blank" rel="noopener noreferrer" className="underline text-teal-700 break-all">{s.title}</a>{s.publisher ? ` — ${s.publisher}` : ''}{s.access_status ? <span className="block text-xs text-gray-500">{s.access_status}{s.reviewed_on ? ` · reviewed ${s.reviewed_on}` : ''}</span> : null}</li>)}</ol> },
          ]} />
          {internshipData.alternative_routes?.links && (
            <div className="mt-6" data-testid="ipf-alt-routes">
              <RouteLinks tid="ipf" links={internshipData.alternative_routes.links.map((l) => ({ ...l, route: l.route || INTERNAL_ROUTES[l.target] }))} />
            </div>
          )}
        </section>

        {/* ── Other companies to watch (review/hold — names only, never linked) ── */}
        {WATCHLIST.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16" data-testid="ipf-watchlist">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-gray-500 mb-2">Explore independently</p>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">Other companies students can monitor</h2>
            <p className="text-sm text-gray-600 mt-2 max-w-2xl leading-relaxed">
              These companies may offer internships or early-career programs at different times. LearnixTa does
              not currently provide direct links for them. Search for the company&rsquo;s official careers website,
              verify the website address carefully and check its current opportunities.
            </p>
            <span className="inline-block mt-4 text-[11px] font-bold tracking-wide uppercase px-3 py-1.5 rounded-full bg-gray-100 border border-gray-200 text-gray-600" data-testid="ipf-watchlist-badge">
              Availability not confirmed
            </span>
            <ul
              className="mt-4 grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2.5 list-none p-0"
              aria-label="Companies students can monitor independently"
              data-testid="ipf-watchlist-names"
            >
              {WATCHLIST.map((name) => (
                <li
                  key={name}
                  className="text-[13px] font-semibold text-gray-700 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 text-center"
                >
                  {name}
                </li>
              ))}
            </ul>
            <p className="text-[13px] text-gray-500 mt-4 leading-relaxed max-w-3xl" data-testid="ipf-watchlist-safety">
              Always confirm that you are visiting the company&rsquo;s genuine careers website. LearnixTa does not
              verify current openings for the companies in this watchlist and does not collect applications,
              documents or fees. Never pay anyone who promises guaranteed internship selection.
            </p>
          </section>
        )}

        {/* ── Disclaimer ───────────────────────────────────── */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16">
          <p className="text-[13px] text-gray-500 leading-relaxed border-t border-gray-200 pt-6" data-testid="ipf-disclaimer">
            This page independently summarizes publicly available internship pathways and is not affiliated with
            or endorsed by the listed organizations. Opportunities, eligibility, benefits and deadlines can
            change. Always verify the live details and apply only through the linked official website.
          </p>
        </section>
      </main>

      {detail && <DetailsModal record={detail} onClose={closeDetail} />}

      <PathwayDisclaimer />
      <Footer />
    </div>
  );
}
